/*
 * UpgradeModal — the single upgrade CTA shared by every PremiumLock,
 * plus fired globally by the axios 402 interceptor.
 *
 * Displays:
 *   - Feature name + required tier
 *   - Compare list of Free vs Premium vs Founding benefits
 *   - Primary CTA → /membership (Stripe checkout)
 *   - Dismiss link (soft-launch note)
 */
import React from "react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { FEATURE_LABELS } from "@/hooks/useTier";

const TIER_LABEL = {
  free:            "Free",
  premium_monthly: "RGM Premium ($9.99/mo)",
  premium_yearly:  "RGM Premium Yearly ($99.99/yr)",
  founding_artist: "Founding Artist",
};

const UpgradeModal = ({ open, onClose, feature, currentTier, requiredTier, gatesEnforced }) => {
  const label = FEATURE_LABELS[feature] || (feature || "").replace(/_/g, " ");
  const needed = TIER_LABEL[requiredTier] || "RGM Premium";
  const isYearly = requiredTier === "premium_yearly";

  return (
    <Dialog open={!!open} onOpenChange={(v) => !v && onClose && onClose()}>
      <DialogContent
        className="max-w-md bg-[#150a24] border-amber-500/40 text-white"
        data-testid="upgrade-modal"
      >
        <DialogHeader>
          <DialogTitle className="text-xl font-black">
            🔒 {label} require {needed}
          </DialogTitle>
          <DialogDescription className="text-purple-200/70">
            You&apos;re currently on the <b>{TIER_LABEL[currentTier] || currentTier}</b> plan.
            {" "}Unlock this + every other creator tool below.
          </DialogDescription>
        </DialogHeader>

        <ul className="text-sm text-gray-200 space-y-1.5 mt-2">
          {isYearly && (
            <li>💼 <b>Business Hub</b> · service marketplace + brand promo</li>
          )}
          <li>🎧 Beat uploads &amp; Marketplace (Producer Mode)</li>
          <li>🎙️ Unlimited Live Beams</li>
          <li>⭐ Featured placement eligibility</li>
          <li>📊 Premium analytics + earnings dashboard</li>
          <li>🤖 AI promo tools + advanced sharing</li>
          <li>🏆 Enter every contest</li>
        </ul>

        <div className="flex flex-wrap gap-2 mt-4">
          <Link to="/membership" onClick={onClose} className="flex-1 min-w-[180px]">
            <Button
              className="w-full bg-gradient-to-r from-amber-500 to-pink-500 hover:from-amber-400 hover:to-pink-400 text-black font-black"
              data-testid="upgrade-modal-cta"
            >
              Upgrade to {needed}
            </Button>
          </Link>
          <Button
            variant="outline"
            onClick={onClose}
            className="border-white/20 text-white"
            data-testid="upgrade-modal-dismiss"
          >
            Not now
          </Button>
        </div>

        {!gatesEnforced && (
          <p className="text-[11px] text-emerald-300/80 mt-3">
            🎉 Soft-launch — Premium features are still open to everyone while we finish QA.
            Upgrade now to lock in launch pricing.
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default UpgradeModal;
